import { useState, useCallback, useRef, useEffect } from 'react'
import { authenticatedFetch, createAuthenticatedEventSource, resolveAccessToken } from '../services/api/apiClient'
import { apiEndpoints } from '../consts'
import { parseAITranscriptResponse, parseExistingAIData } from '../utils/aiResponseParser'
import logger from '../utils/logger'

/**
 * Hook to orchestrate AI generation for all analysis types
 * Generates: transcript, summary, sentiment analysis, QA evaluation
 */
const useAIGeneration = () => {
  const [steps, setSteps] = useState([
    { id: 'transcript', status: 'pending', error: null },
    { id: 'summary', status: 'pending', error: null },
    { id: 'sentiment', status: 'pending', error: null },
    { id: 'qa', status: 'pending', error: null }
  ])
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState(null)
  const [isComplete, setIsComplete] = useState(false)

  const eventSourceRef = useRef(null)
  const streamEpochRef = useRef(0)

  const updateStep = useCallback((stepId, status, errorMsg = null) => {
    setSteps(prev => prev.map(step =>
      step.id === stepId ? { ...step, status, error: errorMsg } : step
    ))
  }, [])

  const isCallInvalid = useCallback((selectedRow) => {
    if (!selectedRow?.ai_summary) return false
    try {
      const summaryData = typeof selectedRow.ai_summary === 'string'
        ? JSON.parse(selectedRow.ai_summary)
        : selectedRow.ai_summary
      return summaryData.valid_call === false
    } catch {
      return false
    }
  }, [])

  const checkExistingData = useCallback((selectedRow) => {
    const existing = {
      transcript: false,
      summary: false,
      sentiment: false,
      qa: false
    }

    if (!selectedRow) return existing

    if (isCallInvalid(selectedRow)) {
      return { transcript: true, summary: true, sentiment: true, qa: true }
    }

    if (selectedRow.ai_transcript) {
      try {
        const { isValid } = parseExistingAIData(selectedRow, 'ai_transcript')
        existing.transcript = isValid
      } catch {
        existing.transcript = false
      }
    }

    if (selectedRow.ai_summary) {
      try {
        const { isValid } = parseExistingAIData(selectedRow, 'ai_summary')
        existing.summary = isValid
      } catch {
        existing.summary = false
      }
    }

    if (selectedRow.sentiment_analysis) {
      existing.sentiment = true
    }

    // Check QA — also treat manually graded calls (imported or manually scored) as having QA
    if (selectedRow.qa_evaluation || (selectedRow.qa_grade && selectedRow.qa_grade !== 'TBD')) {
      existing.qa = true
    }

    return existing
  }, [isCallInvalid])

  const getCallId = useCallback((selectedRow) => {
    if (selectedRow?.id) return selectedRow.id
    if (selectedRow?.call_id) return selectedRow.call_id
    if (selectedRow?.[3]) return selectedRow[3]
    return null
  }, [])

  const isCallEligible = useCallback((selectedRow) => {
    const callDate = selectedRow?.[0] || selectedRow?.call_date
    if (!callDate) return false

    const date = new Date(callDate)
    if (isNaN(date.getTime())) return false

    return date.getFullYear() >= 2025
  }, [])

  const generateTranscriptAndSummary = useCallback(async (callId) => {
    const streamEpoch = streamEpochRef.current
    const accessToken = await resolveAccessToken()

    return new Promise((resolve, reject) => {
      if (streamEpoch !== streamEpochRef.current) return

      eventSourceRef.current = createAuthenticatedEventSource(
        apiEndpoints.aiProcessCallStream,
        { callId },
        {
          onProgress: () => {
          },
          onComplete: (data) => {
            if (data) {
              const { transcript, summary, isValid } = parseAITranscriptResponse(data)
              resolve({ transcript, summary, isValid, data })
            } else {
              reject(new Error('No data received from AI processing'))
            }
          },
          onError: (error) => {
            reject(error)
          }
        },
        accessToken
      )
    })
  }, [])

  const generateSentiment = useCallback(async (callId) => {
    const response = await authenticatedFetch(apiEndpoints.sentimentByCall(callId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || 'Failed to generate sentiment analysis')
    }

    return response.json()
  }, [])

  const generateQA = useCallback(async (callId) => {
    const response = await authenticatedFetch(apiEndpoints.qaEvaluate(callId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ forceRefresh: false })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || 'Failed to generate QA evaluation')
    }

    return response.json()
  }, [])

  const startGeneration = useCallback(async (selectedRow, onComplete) => {
    const callId = getCallId(selectedRow)
    if (!callId) {
      setError('No call ID available')
      return
    }

    if (!isCallEligible(selectedRow)) {
      setError('AI features are only available for calls from 2025 onwards')
      return
    }

    const existing = checkExistingData(selectedRow)

    const initialSteps = [
      { id: 'transcript', status: existing.transcript ? 'skipped' : 'pending', error: null },
      { id: 'summary', status: existing.summary ? 'skipped' : 'pending', error: null },
      { id: 'sentiment', status: existing.sentiment ? 'skipped' : 'pending', error: null },
      { id: 'qa', status: existing.qa ? 'skipped' : 'pending', error: null }
    ]
    setSteps(initialSteps)
    setIsGenerating(true)
    setIsComplete(false)
    setError(null)

    const allSkipped = initialSteps.every(s => s.status === 'skipped')
    if (allSkipped) {
      setIsComplete(true)
      setIsGenerating(false)
      if (onComplete) onComplete()
      return
    }

    try {
      if (!existing.transcript || !existing.summary) {
        updateStep('transcript', 'in-progress')
        if (!existing.summary) updateStep('summary', 'in-progress')

        try {
          const result = await generateTranscriptAndSummary(callId)

          if (result.isValid) {
            if (!existing.transcript) updateStep('transcript', 'complete')
            if (!existing.summary) updateStep('summary', 'complete')
          } else {
            if (!existing.transcript) updateStep('transcript', 'skipped')
            if (!existing.summary) updateStep('summary', 'skipped')
            if (!existing.sentiment) updateStep('sentiment', 'skipped')
            if (!existing.qa) updateStep('qa', 'skipped')
            return
          }
        } catch (err) {
          logger.error('Error generating transcript/summary:', err)
          if (!existing.transcript) updateStep('transcript', 'error', err.message)
          if (!existing.summary) updateStep('summary', 'error', err.message)
        }
      }

      const parallelTasks = []

      if (!existing.sentiment) {
        parallelTasks.push(
          (async () => {
            updateStep('sentiment', 'in-progress')
            try {
              await generateSentiment(callId)
              updateStep('sentiment', 'complete')
            } catch (err) {
              logger.error('Error generating sentiment:', err)
              updateStep('sentiment', 'error', err.message)
            }
          })()
        )
      }

      if (!existing.qa) {
        parallelTasks.push(
          (async () => {
            updateStep('qa', 'in-progress')
            try {
              await generateQA(callId)
              updateStep('qa', 'complete')
            } catch (err) {
              logger.error('Error generating QA:', err)
              updateStep('qa', 'error', err.message)
            }
          })()
        )
      }

      await Promise.all(parallelTasks)

    } catch (err) {
      logger.error('Error in AI generation:', err)
      setError(err.message || 'An error occurred during generation')
    } finally {
      setIsGenerating(false)
      setIsComplete(true)
      if (onComplete) onComplete()
    }
  }, [getCallId, isCallEligible, checkExistingData, updateStep, generateTranscriptAndSummary, generateSentiment, generateQA])

  const reset = useCallback(() => {
    streamEpochRef.current += 1
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    setSteps([
      { id: 'transcript', status: 'pending', error: null },
      { id: 'summary', status: 'pending', error: null },
      { id: 'sentiment', status: 'pending', error: null },
      { id: 'qa', status: 'pending', error: null }
    ])
    setIsGenerating(false)
    setIsComplete(false)
    setError(null)
  }, [])

  const needsGeneration = useCallback((selectedRow) => {
    if (!selectedRow) return false
    if (!isCallEligible(selectedRow)) return false

    const existing = checkExistingData(selectedRow)
    return !existing.transcript || !existing.summary || !existing.sentiment || !existing.qa
  }, [isCallEligible, checkExistingData])

  useEffect(() => {
    return () => {
      streamEpochRef.current += 1
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
    }
  }, [])

  return {
    steps,
    isGenerating,
    isComplete,
    error,
    startGeneration,
    reset,
    needsGeneration,
    checkExistingData
  }
}

export default useAIGeneration
